package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.style.TextOverflow
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import kotlin.math.roundToInt

/**
 * Rich business card for listing screens (Stitch 19bc25c5, "Recommended"/sponsored slot): banner
 * image with a verified pill (top-start) and favorite circle (top-end) overlaid, then name + saffron
 * star rating, category line, location line, and an action row (primary action + Call + Directions).
 * [image] slot draws the photo (placeholder until wired). All labels are passed in localized.
 */
@Composable
fun DirectoryBusinessCard(
    name: String,
    category: String,
    rating: Float,
    primaryActionLabel: String,
    onPrimaryAction: () -> Unit,
    callContentDescription: String,
    directionsContentDescription: String,
    // Null hides the action — a listing with no phone / no exact address shows no dead button.
    onCall: (() -> Unit)? = null,
    onDirections: (() -> Unit)? = null,
    modifier: Modifier = Modifier,
    /** Localized "New" label; when set it replaces the star rating (zero-review listings). */
    newLabel: String? = null,
    locationLabel: String? = null,
    verifiedLabel: String? = null,
    verifiedContentDescription: String? = null,
    openLabel: String? = null,
    isOpen: Boolean = true,
    sponsoredLabel: String? = null,
    /** Localized "Pro" label — shown when the listing's owner has an active Mahaleh Pro subscription. */
    proLabel: String? = null,
    favorite: Boolean = false,
    favoriteContentDescription: String? = null,
    onToggleFavorite: (() -> Unit)? = null,
    onClick: (() -> Unit)? = null,
    coverIcon: String = "storefront",
    coverImageRes: org.jetbrains.compose.resources.DrawableResource? = null,
    image: @Composable (() -> Unit)? = null,
) {
    AppCard(modifier = modifier.fillMaxWidth(), onClick = onClick) {
        Column {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .aspectRatio(16f / 9f)
                    .clip(AppTheme.shapes.card),
            ) {
                if (image != null) image() else BusinessCover(iconName = coverIcon, placeholderImageRes = coverImageRes)
                Row(
                    modifier = Modifier.fillMaxWidth().padding(AppTheme.spacing.md),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.Top,
                ) {
                    Column(verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs)) {
                        if (verifiedLabel != null) VerifiedPill(verifiedLabel, verifiedContentDescription)
                        if (sponsoredLabel != null) SponsoredBadge(label = sponsoredLabel)
                        if (proLabel != null) AccentBadge(label = proLabel)
                    }
                    if (onToggleFavorite != null) {
                        Box(
                            modifier = Modifier
                                .size(AppTheme.sizes.iconCircle)
                                .background(AppTheme.colors.surface, AppTheme.shapes.pill)
                                .clickable(onClick = onToggleFavorite),
                            contentAlignment = Alignment.Center,
                        ) {
                            AppIcon(
                                name = "favorite",
                                size = AppTheme.sizes.iconMd,
                                tint = if (favorite) AppTheme.colors.primary else AppTheme.colors.onSurfaceVariant,
                                filled = favorite,
                                contentDescription = favoriteContentDescription,
                            )
                        }
                    }
                }
            }
            Column(
                modifier = Modifier.padding(AppTheme.spacing.lg),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
                ) {
                    Text(
                        text = name,
                        style = AppTheme.typography.headlineMd,
                        color = AppTheme.colors.onSurface,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f),
                    )
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs),
                    ) {
                        if (newLabel != null) {
                            AccentBadge(label = newLabel)
                        } else {
                            AppIcon(name = "star", size = AppTheme.sizes.iconSm, tint = AppTheme.colors.secondary, filled = true)
                            Text(
                                text = ratingLabel(rating),
                                style = AppTheme.typography.labelMd,
                                color = AppTheme.colors.secondary,
                            )
                        }
                    }
                }
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
                ) {
                    Text(
                        text = category,
                        style = AppTheme.typography.bodySm,
                        color = AppTheme.colors.onSurfaceVariant,
                    )
                    if (openLabel != null) {
                        Text(
                            text = openLabel,
                            style = AppTheme.typography.labelSm,
                            color = if (isOpen) AppTheme.colors.success else AppTheme.colors.alert,
                        )
                    }
                }
                if (locationLabel != null) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
                    ) {
                        AppIcon(name = "location_on", size = AppTheme.sizes.iconSm, tint = AppTheme.colors.onSurfaceVariant)
                        Text(
                            text = locationLabel,
                            style = AppTheme.typography.bodySm,
                            color = AppTheme.colors.onSurfaceVariant,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                        )
                    }
                }
                Row(
                    modifier = Modifier.padding(top = AppTheme.spacing.sm),
                    horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    AppButton(
                        text = primaryActionLabel,
                        onClick = onPrimaryAction,
                        modifier = Modifier.weight(1f),
                    )
                    if (onCall != null) OutlinedIconAction("call", callContentDescription, onCall)
                    if (onDirections != null) OutlinedIconAction("directions", directionsContentDescription, onDirections)
                }
            }
        }
    }
}

/** White "verified" pill overlaid on the cover image (Stitch: white/90 chip, teal check + label). */
@Composable
private fun VerifiedPill(label: String, contentDescription: String?) {
    Row(
        modifier = Modifier
            .background(AppTheme.colors.surface, AppTheme.shapes.sm)
            .padding(horizontal = AppTheme.spacing.sm, vertical = AppTheme.spacing.xs),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
    ) {
        AppIcon(
            name = "verified",
            size = AppTheme.sizes.iconSm,
            tint = AppTheme.colors.primary,
            filled = true,
            contentDescription = contentDescription,
        )
        Text(text = label, style = AppTheme.typography.labelSm, color = AppTheme.colors.primary)
    }
}

/** One-decimal display value for a rating (e.g. 4.75f → "4.8"). */
internal fun ratingLabel(rating: Float): String {
    val rounded = (rating * 10).roundToInt()
    return "${rounded / 10}.${rounded % 10}"
}

@Composable
internal fun OutlinedIconAction(iconName: String, contentDescription: String, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .size(AppTheme.sizes.minTouchTarget)
            .clip(AppTheme.shapes.md)
            .border(AppTheme.sizes.borderWidth, AppTheme.colors.outlineVariant, AppTheme.shapes.md)
            .clickable(onClick = onClick),
        contentAlignment = Alignment.Center,
    ) {
        AppIcon(
            name = iconName,
            size = AppTheme.sizes.iconMd,
            tint = AppTheme.colors.primary,
            contentDescription = contentDescription,
        )
    }
}
